import axios from "axios";
import {
  getConnectivityDiagnosticsSnapshot,
  getRuntimeOriginConfig,
  inspectRequestForMixedContent,
  rebaseKnownLocalBackendUrl,
  recordMixedContentEvent,
} from "@/lib/runtimeOrigin";
import { isDemoMode } from "@/services/mode";
import { createDemoAdapter } from "@/services/demo/adapter";

// ✅ Reliable Electron detection
const isElectron =
  typeof window !== "undefined" &&
  window.JEWEL_FLOW_ENV &&
  window.JEWEL_FLOW_ENV.isElectron === true;

const runtimeOriginConfig = getRuntimeOriginConfig();
let API_BASE = runtimeOriginConfig.apiBase;
let FILE_BASE = runtimeOriginConfig.fileBase;
const ENV_BACKEND_URL = String(process.env.REACT_APP_BACKEND_URL || "").trim();
const LOCAL_HTTP_BACKEND = runtimeOriginConfig.localBackendHttpUrl;
const LOCAL_HTTPS_BACKEND = runtimeOriginConfig.localBackendHttpsUrl;

// Redundant overrides removed - API_BASE/FILE_BASE are now correctly resolved by getRuntimeOriginConfig()

// Axios instance
const api = axios.create({
  baseURL: API_BASE,
  timeout: 20000,
  withCredentials: true,
});

// ✅ Definitive Gateway enforcement: If on port 8013, we MUST use same-origin /api to avoid CORS
if (typeof window !== "undefined" && window.location.port === "8013") {
  api.defaults.baseURL = "/api";
  API_BASE = "/api";
  console.log("[JewelFlow API] Gateway mode detected (port 8013). Enforcing same-origin API base.");
}

// ✅ DEMO MODE: swap the axios adapter so every request is served by the
// in-browser mock data provider (services/demo) instead of the network. The UI,
// pages, components and the rest of this file are untouched — only the data
// provider changes. This branch is dead code in production/preview builds.
if (isDemoMode()) {
  api.defaults.adapter = createDemoAdapter();
  // eslint-disable-next-line no-console
  console.log("[JewelFlow API] DEMO MODE active — using in-browser mock data provider.");
}

// Global GET de-duplication to avoid duplicate page-load request storms.
const GET_DEDUPE_TTL_MS = 2500;
const inflightGetRequests = new Map();
const GET_RESPONSE_CACHE_TTL_MS = 8000;
const getResponseCache = new Map();

const cloneCachedResponse = (resp) => ({
  ...resp,
  data:
    resp?.data && typeof structuredClone === "function"
      ? structuredClone(resp.data)
      : resp?.data,
});

const clearGetCaches = () => {
  inflightGetRequests.clear();
  getResponseCache.clear();
};

export function clearApiGetCache() {
  clearGetCaches();
}

const originalApiGet = api.get.bind(api);
api.get = (url, config = {}) => {
  if (config?.skipDedupe) {
    return originalApiGet(url, config);
  }

  const now = Date.now();
  const method = "get";
  const key = JSON.stringify({
    method,
    url: String(url || ""),
    params: config?.params || null,
    tenant: (() => {
      try {
        return String(localStorage.getItem("jewelflow_active_business_id") || localStorage.getItem("mmj_active_business_id") || "");
      } catch {
        return "";
      }
    })(),
  });

  const cachedResponse = getResponseCache.get(key);
  if (cachedResponse && now - cachedResponse.ts <= GET_RESPONSE_CACHE_TTL_MS) {
    return Promise.resolve(cloneCachedResponse(cachedResponse.response));
  }

  const cached = inflightGetRequests.get(key);
  if (cached && now - cached.ts <= GET_DEDUPE_TTL_MS) {
    return cached.promise;
  }

  const promise = originalApiGet(url, config)
    .then((response) => {
      getResponseCache.set(key, { ts: Date.now(), response });
      return response;
    })
    .finally(() => {
      const entry = inflightGetRequests.get(key);
      if (!entry || entry.promise !== promise) return;
      setTimeout(() => {
        const latest = inflightGetRequests.get(key);
        if (latest && latest.promise === promise) {
          inflightGetRequests.delete(key);
        }
      }, GET_DEDUPE_TTL_MS);
    });

  inflightGetRequests.set(key, { promise, ts: now });
  return promise;
};

const ACCESS_TOKEN_KEY = "mmj_token";
const REFRESH_TOKEN_KEY = "mmj_refresh_token";
const SESSION_ID_KEY = "mmj_session_id";
const USER_KEY = "mmj_user";
const DEVICE_ID_KEY = "mmj_device_id";
const ACCESS_ROUTE_KEY = "mmj_access_route_decision";
const LAST_ACTIVITY_KEY = "mmj_last_activity_at";
const SESSION_TIMEOUT_DEBUG_KEY = "mmj_session_timeout_debug";
const AUTH_STATE_EVENT = "mmj-auth-state-changed";
const ACCESS_ROUTE_EVENT = "mmj-access-route-updated";

function readStoredJson(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

const runtimeAuth = {
  accessToken: (() => {
    try {
      return String(localStorage.getItem(ACCESS_TOKEN_KEY) || "").trim();
    } catch {
      return "";
    }
  })(),
  refreshToken: (() => {
    try {
      return String(localStorage.getItem(REFRESH_TOKEN_KEY) || "").trim();
    } catch {
      return "";
    }
  })(),
  sessionId: (() => {
    try {
      return String(localStorage.getItem(SESSION_ID_KEY) || "").trim();
    } catch {
      return "";
    }
  })(),
  user: readStoredJson(USER_KEY, null),
};
let authStateVersion = 0;
let deviceLocationLabel = "";
let deviceLocationResolvedAt = 0;
let deviceLocationPromise = null;
const DEVICE_LOCATION_CACHE_KEY = "mmj_device_location_label";
const DEVICE_LOCATION_CACHE_TS_KEY = "mmj_device_location_label_ts";
const DEVICE_LOCATION_CACHE_TTL_MS = 30 * 60 * 1000;

function dispatchAuthStateChanged(reason = "updated") {
  authStateVersion += 1;
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent(AUTH_STATE_EVENT, {
        detail: {
          reason,
          version: authStateVersion,
          authenticated: Boolean(runtimeAuth.accessToken || runtimeAuth.sessionId),
          sessionId: String(runtimeAuth.sessionId || "").trim(),
        },
      })
    );
  } catch {
    // ignore event dispatch failures
  }
}

function dispatchAccessRouteChanged(reason = "updated", decision = null) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent(ACCESS_ROUTE_EVENT, {
        detail: {
          reason,
          decision: decision && typeof decision === "object" ? decision : null,
        },
      })
    );
  } catch {
    // ignore event dispatch failures
  }
}

function captureAuthStateSnapshot() {
  return {
    version: authStateVersion,
    accessToken: String(runtimeAuth.accessToken || "").trim(),
    sessionId: String(runtimeAuth.sessionId || "").trim(),
  };
}

function isAuthStateSnapshotCurrent(snapshot = null) {
  if (!snapshot || typeof snapshot !== "object") return true;
  if (Number(snapshot.version || 0) !== authStateVersion) return false;
  if (String(snapshot.accessToken || "").trim() !== String(runtimeAuth.accessToken || "").trim()) return false;
  if (String(snapshot.sessionId || "").trim() !== String(runtimeAuth.sessionId || "").trim()) return false;
  return true;
}

function getDeviceId() {
  // Prefer the OS-level machine fingerprint exposed by Electron preload.
  // Survives app reinstall, localStorage clear, and DB wipe so the same PC
  // does not register multiple times under different UUIDs.
  try {
    const fromElectron =
      (typeof window !== "undefined" && window.JEWEL_FLOW_ENV && String(window.JEWEL_FLOW_ENV.machineId || "").trim()) || "";
    if (fromElectron) {
      try {
        const cached = String(localStorage.getItem(DEVICE_ID_KEY) || "").trim();
        if (cached !== fromElectron) localStorage.setItem(DEVICE_ID_KEY, fromElectron);
      } catch {
        // localStorage may be unavailable; the header still goes out from JEWEL_FLOW_ENV.
      }
      return fromElectron;
    }
  } catch {
    // ignore; fall through to localStorage path
  }
  try {
    const existing = String(localStorage.getItem(DEVICE_ID_KEY) || "").trim();
    if (existing) return existing;
    const generated =
      (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function" && crypto.randomUUID()) ||
      `mmj-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(DEVICE_ID_KEY, generated);
    return generated;
  } catch {
    return "default-device";
  }
}

function getDeviceMetaHeaders() {
  try {
    const platform =
      (typeof navigator !== "undefined" && (navigator.userAgentData?.platform || navigator.platform || navigator.userAgent)) ||
      "";
    const appVersion =
      (typeof window !== "undefined" && window.JEWEL_FLOW_ENV && (window.JEWEL_FLOW_ENV.appVersion || window.JEWEL_FLOW_ENV.version)) || "";
    const deviceName =
      (typeof window !== "undefined" && window.JEWEL_FLOW_ENV && (window.JEWEL_FLOW_ENV.deviceName || "")) || "";
    const electronPlatform =
      (typeof window !== "undefined" && window.JEWEL_FLOW_ENV && (window.JEWEL_FLOW_ENV.platform || "")) || "";
    return {
      "x-mmj-device-name": String(deviceName || "").slice(0, 120),
      "x-mmj-platform": String(electronPlatform || platform || "").slice(0, 160),
      "x-mmj-app-version": String(appVersion || "").slice(0, 80),
    };
  } catch {
    return {};
  }
}

async function resolveDeviceLocationLabel() {
  // DEMO: never call the external geo-IP service — no request may leave the browser.
  if (isDemoMode()) return "";
  const now = Date.now();
  if (deviceLocationLabel && now - deviceLocationResolvedAt < DEVICE_LOCATION_CACHE_TTL_MS) {
    return deviceLocationLabel;
  }
  try {
    const cached = String(localStorage.getItem(DEVICE_LOCATION_CACHE_KEY) || "").trim();
    const cachedTs = Number(localStorage.getItem(DEVICE_LOCATION_CACHE_TS_KEY) || 0);
    if (cached && cachedTs && now - cachedTs < DEVICE_LOCATION_CACHE_TTL_MS) {
      deviceLocationLabel = cached;
      deviceLocationResolvedAt = cachedTs;
      return cached;
    }
  } catch {
    // ignore storage read errors
  }
  if (deviceLocationPromise) return deviceLocationPromise;
  deviceLocationPromise = (async () => {
    try {
      const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
      const timeoutId = ctrl ? setTimeout(() => ctrl.abort(), 1200) : null;
      const resp = await fetch("https://ipapi.co/json/", {
        method: "GET",
        signal: ctrl ? ctrl.signal : undefined,
      });
      if (timeoutId) clearTimeout(timeoutId);
      if (!resp.ok) return "";
      const data = await resp.json();
      const city = String(data?.city || "").trim();
      const region = String(data?.region || "").trim();
      const country = String(data?.country_name || data?.country || "").trim();
      const label = [city, region, country].filter(Boolean).join(", ").slice(0, 180);
      if (label) {
        deviceLocationLabel = label;
        deviceLocationResolvedAt = Date.now();
        try {
          localStorage.setItem(DEVICE_LOCATION_CACHE_KEY, label);
          localStorage.setItem(DEVICE_LOCATION_CACHE_TS_KEY, String(deviceLocationResolvedAt));
        } catch {
          // ignore storage write errors
        }
      }
      return label;
    } catch {
      return "";
    } finally {
      deviceLocationPromise = null;
    }
  })();
  return deviceLocationPromise;
}

function setAuthTokens({ access_token, refresh_token, session_id, user } = {}) {
  runtimeAuth.accessToken = access_token ? String(access_token) : "";
  runtimeAuth.refreshToken = refresh_token ? String(refresh_token) : "";
  runtimeAuth.sessionId = session_id ? String(session_id) : "";
  runtimeAuth.user = user && typeof user === "object" ? user : runtimeAuth.user;
  try {
    if (runtimeAuth.accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, runtimeAuth.accessToken);
    else localStorage.removeItem(ACCESS_TOKEN_KEY);
    if (runtimeAuth.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, runtimeAuth.refreshToken);
    else localStorage.removeItem(REFRESH_TOKEN_KEY);
    if (runtimeAuth.sessionId) localStorage.setItem(SESSION_ID_KEY, runtimeAuth.sessionId);
    else localStorage.removeItem(SESSION_ID_KEY);
    if (runtimeAuth.user && typeof runtimeAuth.user === "object") {
      localStorage.setItem(USER_KEY, JSON.stringify(runtimeAuth.user));
    } else {
      localStorage.removeItem(USER_KEY);
    }
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    localStorage.removeItem(SESSION_TIMEOUT_DEBUG_KEY);
  } catch {
    // ignore storage errors
  }
  dispatchAuthStateChanged("authenticated");
}

function clearAuthTokens() {
  runtimeAuth.accessToken = "";
  runtimeAuth.refreshToken = "";
  runtimeAuth.sessionId = "";
  runtimeAuth.user = null;
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(SESSION_ID_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ACCESS_ROUTE_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    localStorage.removeItem(SESSION_TIMEOUT_DEBUG_KEY);
  } catch {
    // ignore storage errors
  }
  dispatchAuthStateChanged("cleared");
  dispatchAccessRouteChanged("cleared", null);
}

function storeAccessRouteDecision(decision) {
  const normalized =
    decision && typeof decision === "object"
      ? {
          ...decision,
          stored_at: new Date().toISOString(),
        }
      : null;
  try {
    if (normalized) localStorage.setItem(ACCESS_ROUTE_KEY, JSON.stringify(normalized));
    else localStorage.removeItem(ACCESS_ROUTE_KEY);
    if (typeof window !== "undefined") {
      window.__MMJAccessRouteDecision = normalized;
    }
  } catch {
    // ignore storage errors
  }
  dispatchAccessRouteChanged(normalized ? "updated" : "cleared", normalized);
  return normalized;
}

function getStoredAccessRouteDecision() {
  return readStoredJson(ACCESS_ROUTE_KEY, null);
}

function getAccessRouteBlockedMessage(decision) {
  const displayMessage = String(decision?.display_message || "").trim();
  if (displayMessage) return displayMessage;
  const reason = String(decision?.reason_code || decision?.blocked_reason || "").trim().toLowerCase();
  if (reason === "pending_approval") return "Your business access is pending admin approval.";
  if (reason === "no_store_networks_configured") return "This account is restricted to the local business network, but the network has not been configured yet.";
  if (reason === "local_network_only" || reason === "client_ip_unavailable") {
    return "This account is limited to the approved local network. Connect to the allowed business network and try again.";
  }
  if (reason === "device_pending_approval") return "This device is waiting for admin approval before it can access the business.";
  if (reason === "device_not_registered") return "This device is not approved for this business account yet.";
  if (reason === "device_blocked" || reason === "device_revoked") return "This device is blocked for this business account.";
  return "Access to this business is currently blocked. Please contact your admin.";
}

async function fetchAndStoreAccessRouteDecision(options = {}) {
  const {
    suppressLogoutOnUnauthorized = false,
    timeout,
  } = options;
  try {
    const response = await api.get("/auth/access-route", {
      skipDedupe: true,
      timeout,
      __skipAccessRouteRefresh: true,
      __suppressAuthLogout: suppressLogoutOnUnauthorized,
    });
    const storedDecision = storeAccessRouteDecision(response?.data || null);
    return storedDecision;
  } catch (error) {
    const status = Number(error?.response?.status || 0);
    if (status === 401 && suppressLogoutOnUnauthorized) {
      return null;
    }
    if (status === 401 || status === 403) {
      storeAccessRouteDecision(null);
    }
    throw error;
  }
}

const EDIT_METHODS = new Set(["post", "put", "patch", "delete"]);
const SENSITIVE_VAULT_PREFIXES = [
  "/local-security/setup",
  "/local-security/restore",
  "/admin/migration",
  "/local-admin/tester-purge-data",
];
const BYPASS_PREFIXES = [
  "/health",
  "/auth/login",
  "/auth/logout",
  "/auth/refresh",
  "/auth/google/prefill",
  "/auth/register",
  "/auth/admin-first-login",
  "/auth/request-password-reset",
  "/auth/verify-password-reset",
  "/auth/heartbeat",
  "/auth/update-profile",
  "/auth/change-password",
  "/auth/update-theme",
  "/auth/request-pin-otp",
  "/auth/verify-pin",
  "/client-log",
  "/push/",
  "/assistant/",
  "/local-security/",
];
const TENANT_BOOTSTRAP_BYPASS_PREFIXES = [
  "/health",
  "/auth/google/prefill",
  "/client-log",
  "/local-security/status",
  "/local-security/setup",
  "/local-security/unlock",
  "/local-security/unlock-saved",
  "/local-security/lock",
  "/local-security/businesses",
  "/local-security/businesses/select",
  "/local-security/transfer-package",
];
const ENABLE_TENANT_PATH_PREFIX = true;
const SECTION_ACCESS_FALLBACKS = {
  "/storage": ["/items"],
  "/bulk-inventory": ["/items"],
  "/verification": ["/items"],
};
const SECTION_PREFIX_MAP = [
  ["/customers", "/customers"],
  ["/suppliers", "/customers"],
  ["/items", "/items"],
  ["/item-codes", "/items"],
  ["/stone-gallery", "/items"],
  ["/storage-units", "/storage"],
  ["/bulk-inventory", "/bulk-inventory"],
  ["/verification", "/verification"],
  ["/invoices", "/invoices"],
  ["/coupons", "/invoices"],
  ["/purchases", "/purchases"],
  ["/purchase-returns", "/purchases"],
  ["/expense-wallet", "/purchases"],
  ["/payments", "/customer-balances"],
  ["/customer-balance-adjustments", "/customer-balances"],
  ["/customer-metal-storage", "/customer-balances"],
  ["/customer-ledger", "/customer-balances"],
  ["/supplier-balance-adjustments", "/supplier-balances"],
  ["/supplier-metal-storage", "/supplier-balances"],
  ["/supplier-ledger", "/supplier-balances"],
  ["/suppliers/payments", "/supplier-balances"],
  ["/karigars", "/karigars"],
  ["/karigar-transactions", "/karigars"],
  ["/karigar-orders", "/karigars"],
  ["/karigar-consumables", "/karigars"],
  ["/repairs", "/repairs"],
  ["/reports", "/reports"],
  ["/customer-selections", "/customer-relation"],
  ["/customer-approvals", "/customer-relation"],
  ["/coupon-campaigns", "/customer-relation"],
  ["/customers/occasion-reminders", "/customer-relation"],
  ["/gold-rate", "/settings/gold-rate"],
  ["/dashboard/tasks", "/"],
];
const CSRF_COOKIE_NAME = "jewel_flow_csrf_token";

function normalizePath(url = "") {
  let path = String(url || "").trim();
  if (!path) return "/";
  if (path.startsWith("http://") || path.startsWith("https://")) {
    try {
      path = new URL(path).pathname || "/";
      if (path.startsWith("/api")) path = path.slice(4) || "/";
    } catch {
      return "/";
    }
  }
  if (!path.startsWith("/")) path = `/${path}`;
  if (path.startsWith("/api")) path = path.slice(4) || "/";
  return path.replace(/\/+$/, "") || "/";
}

function getCookieValue(name) {
  if (typeof document === "undefined") return "";
  const key = `${name}=`;
  const parts = String(document.cookie || "").split(";");
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed.startsWith(key)) {
      return decodeURIComponent(trimmed.slice(key.length));
    }
  }
  return "";
}

function getActiveTenantId() {
  const fromRuntimeUser = String(runtimeAuth.user?.business_id || "").trim();
  if (fromRuntimeUser && fromRuntimeUser.toLowerCase() !== "default") return fromRuntimeUser;
  const direct = String(
    localStorage.getItem("jewelflow_active_business_id") ||
    localStorage.getItem("mmj_active_business_id") ||
    ""
  ).trim();
  if (direct && direct.toLowerCase() !== "default") return direct;
  return "";
}

function isTenantPrefixedPath(path = "") {
  const parts = String(path || "").split("/").filter(Boolean);
  return parts.length >= 2 && parts[0] !== "api";
}

function withTenantPrefix(path = "", tenantId = "") {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `/${tenantId}${cleanPath === "/" ? "" : cleanPath}`;
}

function resolveSectionForPath(path) {
  for (const [prefix, sectionKey] of SECTION_PREFIX_MAP) {
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      return sectionKey;
    }
  }
  return null;
}

function hasEditAccessForSection(sectionKey) {
  try {
    const user = runtimeAuth.user;
    if (!user) return true;
    const role = String(user?.role || "").toLowerCase();
    if (role === "admin") return true;
    const candidates = [sectionKey, ...(SECTION_ACCESS_FALLBACKS[sectionKey] || [])]
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    const sectionPermissions =
      user?.section_permissions && typeof user.section_permissions === "object"
        ? user.section_permissions
        : {};
    if (Object.keys(sectionPermissions).length > 0) {
      return candidates.some((candidate) => {
        const mode = String(sectionPermissions?.[candidate] || "").toLowerCase();
        return mode === "edit" || mode === "view_edit";
      });
    }

    const allowed = Array.isArray(user?.allowed_sections) ? user.allowed_sections : [];
    if (allowed.length === 0) return true;
    return candidates.some((candidate) => allowed.includes(candidate));
  } catch {
    return true;
  }
}

function isSensitiveVaultFlow(path = "") {
  return SENSITIVE_VAULT_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(prefix)
  );
}

let rememberedUnlockAttempt = null;
let authExpiryRedirecting = false;
let refreshInFlight = null;
let backendFailoverActivated = false;
let backendFailoverProbePromise = null;

function isStaleAuthResponse(config = {}) {
  const requestSessionId = String(config?.__mmjSessionId || "").trim();
  const requestAccessToken = String(config?.__mmjAccessToken || "").trim();
  const currentSessionId = String(runtimeAuth.sessionId || "").trim();
  const currentAccessToken = String(runtimeAuth.accessToken || "").trim();
  if (requestSessionId && currentSessionId && requestSessionId !== currentSessionId) return true;
  if (requestAccessToken && currentAccessToken && requestAccessToken !== currentAccessToken) return true;
  return false;
}
const TRANSIENT_NETWORK_CODES = new Set([
  "ERR_NETWORK_CHANGED",
  "ERR_NETWORK",
  "ECONNABORTED",
  "ETIMEDOUT",
]);
const FAILOVER_HEALTH_TIMEOUT_MS = 1800;

function isTransientNetworkError(error) {
  const status = Number(error?.response?.status || 0);
  if (status >= 500 && status <= 599) return true;
  const code = String(error?.code || "").toUpperCase();
  if (TRANSIENT_NETWORK_CODES.has(code)) return true;
  const msg = String(error?.message || "").toLowerCase();
  return (
    msg.includes("network changed") ||
    msg.includes("network error") ||
    msg.includes("timeout") ||
    msg.includes("timed out")
  );
}

async function canReachBackendCandidate(host) {
  const candidate = String(host || "").trim().replace(/\/+$/, "");
  if (!candidate) return false;
  try {
    const res = await axios.get(`${candidate}/api/health`, {
      timeout: FAILOVER_HEALTH_TIMEOUT_MS,
      withCredentials: true,
      headers: {
        "x-mmj-device-id": getDeviceId(),
        ...getDeviceMetaHeaders(),
      },
    });
    return Number(res?.status || 0) >= 200 && Number(res?.status || 0) < 300;
  } catch {
    return false;
  }
}

async function maybeActivateBackendFailover(error) {
  // DEMO: the mock adapter resolves every request; never probe real hosts.
  if (isDemoMode()) return false;
  if (backendFailoverActivated) return false;
  if (runtimeOriginConfig.isHttpsFrontend && !runtimeOriginConfig.allowHttpDevFallback) return false;
  const code = String(error?.code || "").toUpperCase();
  const msg = String(error?.message || "").toLowerCase();
  const isLocalNetworkBootFailure =
    code === "ERR_CONNECTION_REFUSED" ||
    code === "ECONNABORTED" ||
    code === "ETIMEDOUT" ||
    msg.includes("connection refused") ||
    msg.includes("timed out") ||
    msg.includes("timeout");
  if (!isLocalNetworkBootFailure) return false;
  const currentBase = String(api.defaults.baseURL || "");
  if (!currentBase.includes("localhost:8004")) return false;
  if (!backendFailoverProbePromise) {
    backendFailoverProbePromise = (async () => {
      const fallbackCandidates = [runtimeOriginConfig.localGatewayHttpsUrl, ENV_BACKEND_URL, LOCAL_HTTP_BACKEND]
        .map((v) => String(v || "").trim().replace(/\/+$/, ""))
        .filter(Boolean)
        .filter((host, index, all) => all.indexOf(host) === index)
        .filter((host) => !currentBase.startsWith(`${host}/api`));

      for (const nextBaseHost of fallbackCandidates) {
        const reachable = await canReachBackendCandidate(nextBaseHost);
        if (!reachable) continue;
        const nextBase = `${nextBaseHost}/api`;
        api.defaults.baseURL = nextBase;
        FILE_BASE = nextBaseHost;
        backendFailoverActivated = true;
        clearGetCaches();
        console.warn("[JewelFlow API] Switched backend base URL to healthy launcher host:", nextBase);
        return true;
      }
      return false;
    })().finally(() => {
      backendFailoverProbePromise = null;
    });
  }

  return backendFailoverProbePromise;
}

function isRefreshEligiblePath(path = "") {
  if (!path) return false;
  if (path === "/auth/login" || path.startsWith("/auth/login/")) return false;
  if (path === "/auth/refresh" || path.startsWith("/auth/refresh/")) return false;
  if (path === "/auth/register" || path.startsWith("/auth/register/")) return false;
  if (path.startsWith("/local-security/")) return false;
  return true;
}

function getAccessTokenExpiryMs() {
  const token = String(runtimeAuth.accessToken || "").trim();
  if (!token) return null;
  try {
    // JWT format: header.payload.signature
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    const expSeconds = Number(payload?.exp || 0);
    if (!expSeconds || !Number.isFinite(expSeconds)) return null;
    const expiresAtMs = expSeconds * 1000;
    const nowMs = Date.now();
    return Math.max(0, expiresAtMs - nowMs);
  } catch {
    return null;
  }
}

async function refreshAccessToken() {
  const refreshToken = String(runtimeAuth.refreshToken || "").trim();
  if (!refreshToken) throw new Error("Missing refresh token");
  const res = await api.post(
    "/auth/refresh",
    { refresh_token: refreshToken },
    {
      timeout: 10000,
      __skipProactiveRefresh: true,
    }
  );
  if (!res?.data?.access_token) throw new Error("Refresh failed");
  setAuthTokens({
    access_token: res.data.access_token,
    refresh_token: res.data.refresh_token,
    session_id: res.data.session_id,
  });
  return String(res.data.access_token);
}

async function ensureFreshAccessToken(options = {}) {
  const { minTtlMs = 120000 } = options; // default: refresh if <2 min remaining
  const expiryMs = getAccessTokenExpiryMs();
  if (expiryMs === null || expiryMs >= minTtlMs) {
    return; // token valid or unknown, no refresh needed
  }
  if (!refreshInFlight) {
    refreshInFlight = refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }
  try {
    await refreshInFlight;
  } catch {
    // Refresh failed; fall back to reactive 401 retry in response interceptor.
    // Do not throw — let request proceed; interceptor will catch 401.
  }
}

api.interceptors.request.use(async (config) => {
  const mixedInspection = inspectRequestForMixedContent(config.url || "");
  if (mixedInspection.mixed) {
    recordMixedContentEvent({
      type: "request_blocked",
      url: mixedInspection.resolvedUrl,
      method: String(config.method || "get").toUpperCase(),
    });
    if (!runtimeOriginConfig.allowHttpDevFallback) {
      return Promise.reject(new Error(`Blocked mixed-content backend request: ${mixedInspection.resolvedUrl}`));
    }
  }

  // Proactively refresh access token if expiring soon (unless auth/refresh endpoint).
  const path = normalizePath(config.url || "");
  const isAuthBypassed = BYPASS_PREFIXES.some((prefix) => path === prefix || path.startsWith(prefix));
  const skipProactiveRefresh = Boolean(config?.__skipProactiveRefresh);
  const skipStartupRefreshProbe = path === "/auth/me" || path === "/auth/access-route";
  if (!skipProactiveRefresh && !skipStartupRefreshProbe && !isAuthBypassed && String(runtimeAuth.accessToken || "").trim()) {
    try {
      await ensureFreshAccessToken({ minTtlMs: 120000 });
    } catch {
      // ensureFreshAccessToken does not throw; fallback to reactive 401 retry.
    }
  }

  const token = String(runtimeAuth.accessToken || "").trim();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    config.__mmjAccessToken = token;
    config.__mmjSessionId = String(runtimeAuth.sessionId || "").trim();
  }
  config.headers["x-mmj-device-id"] = getDeviceId();
  const deviceMeta = getDeviceMetaHeaders();
  for (const [key, value] of Object.entries(deviceMeta)) {
    if (value) config.headers[key] = value;
  }
  // Never block API calls on geo lookup; use cached value if present and refresh in background.
  if (deviceLocationLabel) {
    config.headers["x-mmj-location"] = String(deviceLocationLabel).slice(0, 180);
  } else {
    try {
      const cached = String(localStorage.getItem(DEVICE_LOCATION_CACHE_KEY) || "").trim();
      if (cached) {
        config.headers["x-mmj-location"] = cached.slice(0, 180);
        deviceLocationLabel = cached;
      }
    } catch {
      // ignore storage read errors
    }
    resolveDeviceLocationLabel().catch(() => {});
  }

  const method = String(config.method || "get").toLowerCase();
  if (EDIT_METHODS.has(method) && path !== "/auth/login" && path !== "/auth/google") {
    const csrfToken = getCookieValue(CSRF_COOKIE_NAME);
    if (csrfToken) {
      config.headers["x-csrf-token"] = csrfToken;
    }
  }
  const tenantId = getActiveTenantId();
  const tenantBypass = TENANT_BOOTSTRAP_BYPASS_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(prefix + "/")
  );
  if (ENABLE_TENANT_PATH_PREFIX && tenantId && !tenantBypass && !isTenantPrefixedPath(path)) {
    config.url = withTenantPrefix(path, tenantId);
  }
  const isBypassed = BYPASS_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(prefix)
  );

  if (!isBypassed && EDIT_METHODS.has(method)) {
    clearGetCaches();
    const sectionKey = resolveSectionForPath(path);
    if (sectionKey && !hasEditAccessForSection(sectionKey)) {
      const err = new Error(`Edit access is disabled for section ${sectionKey}.`);
      err.response = {
        status: 403,
        data: {
          detail: `Edit access is disabled for section ${sectionKey}.`,
          section: sectionKey,
        },
      };
      return Promise.reject(err);
    }
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const requestUrl = String(error?.config?.url || "");
    const path = normalizePath(requestUrl);
    const originalConfig = error?.config || {};
    const isVaultRoute =
      typeof window !== "undefined" && window.location.hash.includes("/vault-unlock");
    const isLocalSecurityCall = requestUrl.includes("/local-security/");
    const method = String(originalConfig?.method || "get").toLowerCase();
    const authStateSnapshot = captureAuthStateSnapshot();

    // If /local-security/status returns 404, the SQLCipher backend is not running.
    // Only trigger on /status — other local-security endpoints legitimately return 404
    // (e.g. business not found, backup not found). Do NOT treat as vault locked.
    if (status === 404 && path === "/local-security/status" && typeof window !== "undefined") {
      window.__MMJBackendMismatch = true;
      window.dispatchEvent(
        new CustomEvent("mmj-backend-mismatch", {
          detail: { path, status, reason: "local-security/status not found — SQLCipher backend may not be running" },
        })
      );
      return Promise.reject(error);
    }

    if (!originalConfig.__baseFailoverAttempted && await maybeActivateBackendFailover(error)) {
      originalConfig.__baseFailoverAttempted = true;
      originalConfig.baseURL = api.defaults.baseURL;
      return api(originalConfig);
    }

    // Auto-retry idempotent requests once for brief network churn (wifi switch, DNS reset, timeout).
    if (
      method === "get" &&
      !originalConfig.__networkRetryAttempted &&
      isTransientNetworkError(error)
    ) {
      originalConfig.__networkRetryAttempted = true;
      await new Promise((resolve) => setTimeout(resolve, 450));
      return api(originalConfig);
    }

    if (
      status === 401 &&
      isRefreshEligiblePath(path) &&
      !originalConfig.__authRetryAttempted
    ) {
      if (isStaleAuthResponse(originalConfig) || !isAuthStateSnapshotCurrent(authStateSnapshot)) {
        return Promise.reject(error);
      }
      try {
        originalConfig.__authRetryAttempted = true;
        if (!refreshInFlight) {
          refreshInFlight = refreshAccessToken().finally(() => {
            refreshInFlight = null;
          });
        }
        const nextAccessToken = await refreshInFlight;
        if (!isAuthStateSnapshotCurrent(authStateSnapshot)) {
          return Promise.reject(error);
        }
        originalConfig.headers = originalConfig.headers || {};
        originalConfig.headers.Authorization = `Bearer ${nextAccessToken}`;
        originalConfig.headers["x-mmj-device-id"] = getDeviceId();
        return api(originalConfig);
      } catch {
        if (!isAuthStateSnapshotCurrent(authStateSnapshot)) {
          return Promise.reject(error);
        }
        clearGetCaches();
        if (!originalConfig.__suppressAuthLogout) {
          clearAuthTokens();
        }
        if (typeof window !== "undefined" && !authExpiryRedirecting && !originalConfig.__suppressAuthLogout) {
          authExpiryRedirecting = true;
          window.location.hash = "#/login";
          setTimeout(() => {
            authExpiryRedirecting = false;
          }, 1500);
        }
      }
    }

    if (status === 423 && !isVaultRoute && !isLocalSecurityCall && typeof window !== "undefined") {
      if (isStaleAuthResponse(originalConfig)) {
        return Promise.reject(error);
      }
      if (!originalConfig.__vaultRetryAttempted) {
        originalConfig.__vaultRetryAttempted = true;
        try {
          if (!rememberedUnlockAttempt) {
            rememberedUnlockAttempt = api.post("/local-security/unlock-saved", {});
          }
          await rememberedUnlockAttempt;
          rememberedUnlockAttempt = null;
          return api(originalConfig);
        } catch {
          rememberedUnlockAttempt = null;
        }
      }

      clearGetCaches();
      const mode = isSensitiveVaultFlow(path) ? "sensitive" : "general";
      window.location.hash = `#/vault-unlock?mode=${mode}`;
    }

    if (
      status === 401 &&
      typeof window !== "undefined" &&
      (path === "/auth/heartbeat" || path === "/auth/me") &&
      !authExpiryRedirecting
    ) {
      if (isStaleAuthResponse(originalConfig)) {
        return Promise.reject(error);
      }
      authExpiryRedirecting = true;
      try {
        clearGetCaches();
        clearAuthTokens();
      } catch {
        // no-op
      }
      window.location.hash = "#/login";
      setTimeout(() => {
        authExpiryRedirecting = false;
      }, 1500);
    }

    return Promise.reject(error);
  }
);

export default api;
export function setCurrentUser(user) {
  runtimeAuth.user = user && typeof user === "object" ? user : null;
}
export function getCurrentUser() {
  return runtimeAuth.user;
}
export function getAccessToken() {
  return String(runtimeAuth.accessToken || "").trim();
}
export function hasAuthSessionHint() {
  return Boolean(
    String(runtimeAuth.accessToken || "").trim() ||
    String(runtimeAuth.refreshToken || "").trim() ||
    String(runtimeAuth.sessionId || "").trim()
  );
}
export function resetAuthRedirectState() {
  authExpiryRedirecting = false;
}
export function getApiRuntimeDiagnostics() {
  return {
    ...getConnectivityDiagnosticsSnapshot(),
    apiBase: String(api.defaults.baseURL || ""),
    fileBase: String(FILE_BASE || ""),
    backendFailoverActivated,
  };
}
export function resolveAppFileUrl(raw) {
  return rebaseKnownLocalBackendUrl(raw, FILE_BASE || runtimeOriginConfig.fileBase || LOCAL_HTTPS_BACKEND);
}
export {
  ACCESS_ROUTE_EVENT,
  AUTH_STATE_EVENT,
  api,
  FILE_BASE,
  captureAuthStateSnapshot,
  fetchAndStoreAccessRouteDecision,
  clearAuthTokens,
  getAccessRouteBlockedMessage,
  isAuthStateSnapshotCurrent,
  isTransientNetworkError,
  getStoredAccessRouteDecision,
  setAuthTokens,
};
